import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
	collectPhone,
	verifyCollectedPhone,
} from "@/features/auth/services/auth.service";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { getPostAuthLanding } from "@/features/auth/utils/post-auth-redirect";
import { FloatPanel, StudioButton } from "@/features/studio/theme";

export function CollectPhonePage() {
	const navigate = useNavigate();
	const location = useLocation();
	const {
		setPhone: setStorePhone,
		needsOnboarding,
		needsProfileSetup,
		getUserType,
		logout,
	} = useAuthStore();

	const [step, setStep] = useState<"phone" | "otp">("phone");
	const [phone, setPhoneInput] = useState("");
	const [otp, setOtp] = useState(["", "", "", "", "", ""]);
	const [loading, setLoading] = useState(false);
	const [resendTimer, setResendTimer] = useState(0);

	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	// Countdown timer for resend
	useEffect(() => {
		if (resendTimer > 0) {
			const timer = setTimeout(() => setResendTimer((t) => t - 1), 1000);
			return () => clearTimeout(timer);
		}
	}, [resendTimer]);

	const handleSendPhone = async (e: React.FormEvent) => {
		e.preventDefault();
		const clean = phone.replace(/\D/g, "");
		if (clean.length !== 10) {
			toast.error("Enter a valid 10-digit phone number");
			return;
		}
		setLoading(true);
		try {
			const res = await collectPhone(clean);
			if (!res.success) {
				toast.error(res.message || "Could not send the code");
				return;
			}
			setStep("otp");
			setResendTimer(30);
			toast.success("Code sent");
			// Focus first OTP input on next render
			setTimeout(() => inputRefs.current[0]?.focus(), 50);
		} catch {
			toast.error("Could not send the code. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const handleVerifyOtp = async (otpCode: string) => {
		const clean = phone.replace(/\D/g, "");
		setLoading(true);
		try {
			const res = await verifyCollectedPhone(clean, otpCode);
			if (!res.success) {
				toast.error(res.message || "Invalid code");
				setOtp(["", "", "", "", "", ""]);
				inputRefs.current[0]?.focus();
				return;
			}
			setStorePhone(res.data.phone ?? clean);
			toast.success("Phone number added successfully");

			// Determine redirect destination. A `redirectTo` from ProtectedRoute
			// wins: the visitor was already on their way somewhere when the
			// missing phone diverted them here. It is checked first for the same
			// reason verify-otp checks it first, and it is safe ahead of the
			// onboarding cases because ProtectedRoute re-evaluates them on
			// arrival.
			const redirectTo = (location.state as { redirectTo?: string } | null)
				?.redirectTo;
			if (redirectTo) {
				navigate(redirectTo, { replace: true });
			} else if (needsOnboarding()) {
				navigate("/onboarding", { replace: true });
			} else if (needsProfileSetup()) {
				navigate(`/onboarding/${getUserType()}`, { replace: true });
			} else {
				navigate(getPostAuthLanding(getUserType()), { replace: true });
			}
		} catch {
			toast.error("Invalid code. Please try again.");
			setOtp(["", "", "", "", "", ""]);
			inputRefs.current[0]?.focus();
		} finally {
			setLoading(false);
		}
	};

	const handleOtpChange = (index: number, value: string) => {
		if (value.length > 1) {
			value = value.slice(-1);
		}

		const newOtp = [...otp];
		newOtp[index] = value;
		setOtp(newOtp);

		if (value && index < 5) {
			inputRefs.current[index + 1]?.focus();
		}

		if (newOtp.every((digit) => digit) && newOtp.join("").length === 6) {
			handleVerifyOtp(newOtp.join(""));
		}
	};

	const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
		if (e.key === "Backspace" && !otp[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (e: React.ClipboardEvent) => {
		e.preventDefault();
		const pastedData = e.clipboardData
			.getData("text")
			.replace(/\D/g, "")
			.slice(0, 6);
		if (pastedData.length === 6) {
			const newOtp = pastedData.split("");
			setOtp(newOtp);
			handleVerifyOtp(pastedData);
		}
	};

	const handleResend = async () => {
		const clean = phone.replace(/\D/g, "");
		try {
			await collectPhone(clean);
			toast.success("Code resent");
			setResendTimer(30);
			setOtp(["", "", "", "", "", ""]);
			inputRefs.current[0]?.focus();
		} catch {
			toast.error("Failed to resend code");
		}
	};

	const handleCancel = () => {
		// The store clears the local session even when the server call fails,
		// so leaving is right either way.
		void logout().catch(() => {});
		navigate("/login", { replace: true });
	};

	return (
		<div className="flex min-h-screen flex-col items-center justify-center px-4 bg-dashboard-warm-350">
			<div className="w-full max-w-md">
				{/* Logo/Header */}
				<div className="mb-8 text-center">
					<h1 className="font-display font-semibold text-[32px] text-dashboard-text mb-2">
						{step === "phone" ? "Complete your profile" : "Verify code"}
					</h1>
					<p className="font-body text-sm text-dashboard-muted">
						{step === "phone"
							? "Please provide your phone number to secure your account."
							: `Enter the code sent to +91 ${phone}`}
					</p>
				</div>

				<FloatPanel elevation={2} padding="40px 32px">
					{step === "phone" ? (
						<form onSubmit={handleSendPhone} className="space-y-6">
							<div className="flex flex-col gap-1.5">
								<label
									htmlFor="phone"
									className="font-mono text-xs font-semibold tracking-wider uppercase text-dashboard-muted"
								>
									Phone Number
								</label>
								<div className="flex">
									<span className="inline-flex items-center rounded-l-[7px] border border-r-0 border-dashboard-border bg-dashboard-warm-450/40 px-4 text-sm font-body text-dashboard-muted">
										+91
									</span>
									<input
										id="phone"
										type="tel"
										value={phone}
										onChange={(e) => setPhoneInput(e.target.value)}
										placeholder="98765 43210"
										disabled={loading}
										className="flex-1 rounded-r-[7px] border border-dashboard-border bg-dashboard-paper px-4 py-3 text-sm font-body text-dashboard-text placeholder-dashboard-muted focus:border-dashboard-accent focus:outline-none disabled:bg-dashboard-warm-450/20"
									/>
								</div>
							</div>

							<div className="flex flex-col gap-3">
								<StudioButton
									type="submit"
									disabled={loading}
									className="w-full"
								>
									{loading ? "Sending..." : "Send OTP"}
								</StudioButton>
								<button
									type="button"
									onClick={handleCancel}
									className="font-body text-sm text-dashboard-muted hover:text-dashboard-text transition-colors mt-2"
								>
									Cancel and sign out
								</button>
							</div>
						</form>
					) : (
						<div className="space-y-6">
							<div
								className="flex justify-center gap-1.5 sm:gap-2"
								onPaste={handlePaste}
							>
								{otp.map((digit, index) => (
									<input
										// biome-ignore lint/suspicious/noArrayIndexKey: OTP inputs are positional
										key={`otp-${index}`}
										ref={(el) => {
											inputRefs.current[index] = el;
										}}
										type="text"
										inputMode="numeric"
										pattern="[0-9]*"
										maxLength={1}
										value={digit}
										disabled={loading}
										onChange={(e) => handleOtpChange(index, e.target.value)}
										onKeyDown={(e) => handleKeyDown(index, e)}
										className="w-12 h-14 text-center text-xl font-display font-medium rounded-[7px] border border-dashboard-border bg-dashboard-paper focus:border-dashboard-accent focus:outline-none disabled:bg-dashboard-warm-450/20"
									/>
								))}
							</div>

							<div className="flex flex-col items-center gap-4">
								<div className="text-center font-body text-sm text-dashboard-muted">
									{resendTimer > 0 ? (
										<span>Resend code in {resendTimer}s</span>
									) : (
										<button
											type="button"
											onClick={handleResend}
											className="text-dashboard-accent-dark font-semibold hover:underline"
										>
											Resend code
										</button>
									)}
								</div>

								<div className="flex w-full gap-4">
									<StudioButton
										variant="secondary"
										onClick={() => setStep("phone")}
										disabled={loading}
										className="flex-1"
									>
										Back
									</StudioButton>
								</div>
							</div>
						</div>
					)}
				</FloatPanel>
			</div>
		</div>
	);
}
